/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { forwardRef, ForwardedRef } from 'preact/compat';
import { Table } from '../PRIVATE_Table';
import { tableViewStyles } from './themes/TableViewStyles.css';
import { TableProps, TableViewHandle } from './Table.types';
import { TestIdProps } from '../hooks/UNSAFE_useTestId';
import { classNames } from '../utils/UNSAFE_classNames';
import { DimensionProps, dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';

/**
 * Props specific to non-virtualized TableView
 */
type NonVirtualizedTableProps<K, D> = {
  /**
   * The data that is used to render each item in TableView.  If the value is null, then TableView will show nothing
   * if hasMore is false, or loading indicator if hasMore is true.
   */
  data: D[] | null;
  /**
   * A callback function to extract the key which is used to identify the item.
   */
  getRowKey: (data: D) => K;
  /**
   * Whether there is more data available to load.  By default this returns false.
   */
  hasMore?: boolean;
  /**
   * An optional callback function which is invoked when the user scrolls to the end of the table.
   */
  onLoadMore?: () => void;
};

/**
 * Props for the TableView Component
 */
export type Props<K, D, C extends string> = TableProps<K, D, C> &
  NonVirtualizedTableProps<K, D> &
  DimensionProps &
  TestIdProps;

const mergedInterpolations = mergeInterpolations<DimensionProps>([
  ...Object.values(dimensionInterpolations)
]);

/**
 * Component that renders data in rows of columns.
 * This component is non-virtualized and renders all rows provided in the data prop.
 */
export const TableView = forwardRef(
  (
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      getAccessibleRowHeaders,
      children,
      columns,
      columnOrder,
      columnWidths,
      layout = 'contents',
      data,
      getRowKey,
      hasMore = false,
      onLoadMore,
      currentCell,
      onCurrentCellChange,
      currentRowVariant = 'none',
      gridlines = { horizontal: 'visible', vertical: 'hidden' },
      selectionMode = { row: 'none', column: 'none' },
      selected,
      onSelectionChange,
      onRowAction,
      sortCriterion = [],
      onSortCriterionChange,
      horizontalScrollPositionOverride,
      verticalScrollPositionOverride,
      columnResizingIndicator,
      onColumnResizing,
      onColumnResize,
      noDataRenderer,
      testId,
      ...dimensions
    }: Props<string | number, any, string>,
    ref?: ForwardedRef<TableViewHandle<string | number, string>>
  ) => {
    const { ...mergedStyles } = mergedInterpolations(dimensions);
    const tableViewClasses = classNames([tableViewStyles.base]);

    return (
      <div class={tableViewClasses} style={mergedStyles}>
        <Table
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          getAccessibleRowHeaders={getAccessibleRowHeaders}
          children={children}
          columns={columns}
          columnOrder={columnOrder}
          columnWidths={columnWidths}
          layout={layout}
          data={data}
          getRowKey={getRowKey}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          currentCell={currentCell}
          onCurrentCellChange={onCurrentCellChange}
          currentRowVariant={currentRowVariant}
          gridlines={gridlines}
          selectionMode={selectionMode}
          selected={selected}
          onSelectionChange={onSelectionChange}
          onRowAction={onRowAction}
          horizontalScrollPositionOverride={horizontalScrollPositionOverride}
          verticalScrollPositionOverride={verticalScrollPositionOverride}
          sortCriterion={sortCriterion}
          onSortCriterionChange={onSortCriterionChange}
          columnResizingIndicator={columnResizingIndicator}
          onColumnResizing={onColumnResizing}
          onColumnResize={onColumnResize}
          noDataRenderer={noDataRenderer}
          testId={testId}
          ref={ref}
        />
      </div>
    );
  }
);
